import React, { useState, useEffect, useCallback } from 'react';
import {
  getDoctorsBalances,
  getDoctorBalanceDetail,
  markDoctorAppointmentPaid,
  markAllDoctorAppointmentsPaid,
} from '../../../services/adminService';
import {
  Wallet, RefreshCw, AlertCircle, ChevronDown, ChevronRight,
  CheckCircle2, Clock, Phone, CheckCheck,
} from 'lucide-react';

const fmtCOP  = (n) => new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(n || 0);
const fmtDateTime = (d) => d ? new Date(d).toLocaleString('es-CO', { timeZone: 'America/Bogota' }) : '—';
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('es-CO', { timeZone: 'America/Bogota' }) : '—';

// ─── Fila expandible por médico ───────────────────────────────────────────────
const DoctorRow = ({ doctor, onPaid }) => {
  const [expanded, setExpanded] = useState(false);
  const [detail,   setDetail]   = useState(null);
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState(null);
  const [actionId, setActionId] = useState(null);  // appointmentId en curso
  const [allLoading, setAllLoading] = useState(false);

  const loadDetail = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const data = await getDoctorBalanceDetail(doctor.doctorId);
      setDetail(data);
    } catch (e) {
      setError(e?.response?.data?.message || 'Error al cargar detalle');
    } finally {
      setLoading(false);
    }
  }, [doctor.doctorId]);

  const toggle = () => {
    const next = !expanded;
    setExpanded(next);
    if (next && !detail) loadDetail();
  };

  const handleMarkPaid = async (appointmentId) => {
    setActionId(appointmentId); setError(null);
    try {
      await markDoctorAppointmentPaid(appointmentId);
      await loadDetail();
      onPaid();
    } catch (e) {
      setError(e?.response?.data?.message || 'Error al marcar el pago');
    } finally {
      setActionId(null);
    }
  };

  const handleMarkAll = async () => {
    if (!window.confirm(`¿Marcar como pagadas todas las consultas pendientes de ${doctor.name}?`)) return;
    setAllLoading(true); setError(null);
    try {
      await markAllDoctorAppointmentsPaid(doctor.doctorId);
      await loadDetail();
      onPaid();
    } catch (e) {
      setError(e?.response?.data?.message || 'Error al marcar los pagos');
    } finally {
      setAllLoading(false);
    }
  };

  const pendingAppointments = detail?.appointments?.filter(a => a.payoutStatus !== 'paid') || [];

  return (
    <>
      <tr className="hover:bg-gray-50 cursor-pointer" onClick={toggle}>
        <td className="px-4 py-3">
          <div className="flex items-center gap-2">
            {expanded ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
            <div>
              <p className="font-medium text-gray-900">{doctor.name}</p>
              <p className="text-xs text-gray-400">{doctor.email}</p>
            </div>
          </div>
        </td>
        <td className="px-4 py-3 text-gray-600">
          <span className="inline-flex items-center gap-1 text-xs">
            <Phone className="w-3 h-3 text-gray-400" />{doctor.nequiNumber || '—'}
          </span>
        </td>
        <td className="px-4 py-3 text-gray-900 tabular-nums font-medium">{fmtCOP(doctor.pendingBalance)}</td>
        <td className="px-4 py-3 text-gray-500 tabular-nums">{fmtCOP(doctor.paidBalance)}</td>
        <td className="px-4 py-3">
          {doctor.pendingCount > 0 ? (
            <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-700">
              <Clock className="w-3 h-3" />{doctor.pendingCount} pendiente(s)
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-green-100 text-green-700">
              <CheckCircle2 className="w-3 h-3" />Al día
            </span>
          )}
        </td>
        <td className="px-4 py-3 text-right">
          {doctor.pendingCount > 0 && (
            <button
              onClick={(e) => { e.stopPropagation(); handleMarkAll(); }}
              disabled={allLoading}
              className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50 font-medium">
              {allLoading ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <CheckCheck className="w-3.5 h-3.5" />}
              Marcar todos pagados
            </button>
          )}
        </td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={6} className="bg-gray-50 px-4 py-4">
            {error && (
              <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg mb-3">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />{error}
              </div>
            )}
            {loading ? (
              <div className="animate-pulse space-y-2">
                {[...Array(3)].map((_, i) => <div key={i} className="h-8 bg-gray-100 rounded" />)}
              </div>
            ) : !detail ? null : detail.appointments.length === 0 ? (
              <p className="text-sm text-gray-500 py-2">Este médico no tiene consultas completadas y pagadas.</p>
            ) : (
              <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      {['Fecha', 'Paciente', 'Monto neto', 'Estado', 'Fecha pago', ''].map(h => (
                        <th key={h} className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {detail.appointments.map(a => (
                      <tr key={a.appointmentId}>
                        <td className="px-3 py-2 text-gray-600">{fmtDateTime(a.scheduledDate)}</td>
                        <td className="px-3 py-2 text-gray-900">{a.patientName}</td>
                        <td className="px-3 py-2 text-gray-900 tabular-nums">{fmtCOP(a.amount)}</td>
                        <td className="px-3 py-2">
                          {a.payoutStatus === 'paid' ? (
                            <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-green-100 text-green-700">
                              <CheckCircle2 className="w-3 h-3" />Pagado
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-700">
                              <Clock className="w-3 h-3" />Pendiente
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-gray-500">{fmtDate(a.payoutDate)}</td>
                        <td className="px-3 py-2 text-right">
                          {a.payoutStatus !== 'paid' && (
                            <button
                              onClick={() => handleMarkPaid(a.appointmentId)}
                              disabled={actionId === a.appointmentId}
                              className="inline-flex items-center gap-1.5 text-xs px-3 py-1 bg-teal-50 text-teal-700 rounded-lg hover:bg-teal-100 disabled:opacity-50 font-medium">
                              {actionId === a.appointmentId ? <RefreshCw className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
                              Marcar pagado
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {pendingAppointments.length === 0 && (
                  <p className="text-xs text-gray-400 px-3 py-2 border-t border-gray-100">Todas las consultas de este médico están pagadas.</p>
                )}
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
};

// ─── Sección principal ────────────────────────────────────────────────────────
export default function DoctorPayoutsSection() {
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const data = await getDoctorsBalances();
      setDoctors(data);
    } catch (e) {
      setError(e?.response?.data?.message || 'Error al cargar balances');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const totalPending = doctors.reduce((s, d) => s + (d.pendingBalance || 0), 0);
  const totalPaid    = doctors.reduce((s, d) => s + (d.paidBalance || 0), 0);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-gray-900">Pagos a médicos</h2>
          <p className="text-sm text-gray-500">Gestiona las transferencias (Nequi) a los médicos por sus consultas.</p>
        </div>
        <button onClick={load} disabled={loading}
          className="flex items-center gap-2 text-sm text-gray-500 hover:text-teal-600 border border-gray-200 rounded-lg px-3 py-1.5 hover:border-teal-300 transition-colors disabled:opacity-40">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />Actualizar
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center gap-3 mb-1">
            <div className="w-9 h-9 rounded-full bg-yellow-100 flex items-center justify-center">
              <Clock className="w-4 h-4 text-yellow-600" />
            </div>
            <p className="text-sm text-gray-500">Total por pagar</p>
          </div>
          <p className="text-2xl font-bold text-gray-900">{fmtCOP(totalPending)}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center gap-3 mb-1">
            <div className="w-9 h-9 rounded-full bg-green-100 flex items-center justify-center">
              <CheckCircle2 className="w-4 h-4 text-green-600" />
            </div>
            <p className="text-sm text-gray-500">Total pagado</p>
          </div>
          <p className="text-2xl font-bold text-gray-900">{fmtCOP(totalPaid)}</p>
        </div>
      </div>

      {error ? (
        <div className="flex flex-col items-center justify-center h-48 gap-3 text-red-500">
          <AlertCircle className="w-8 h-8" />
          <p className="text-sm">{error}</p>
          <button onClick={load} className="text-sm text-teal-600 hover:underline">Reintentar</button>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          {loading ? (
            <div className="animate-pulse p-4 space-y-3">
              {[...Array(5)].map((_, i) => <div key={i} className="h-12 bg-gray-100 rounded-lg" />)}
            </div>
          ) : doctors.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-gray-400">
              <Wallet className="w-10 h-10 mb-3 text-gray-200" />
              <p className="text-sm">No hay médicos con consultas registradas</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    {['Médico', 'Nequi', 'Por pagar', 'Pagado', 'Estado', ''].map(h => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {doctors.map(doc => (
                    <DoctorRow key={doc.doctorId} doctor={doc} onPaid={load} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
